import axios from 'axios';
import { captureError } from './errorReporting.js';
import { showLlmBudgetNotice } from './llmBudget.js';
import { normalizeVideoCollection } from '../utils/videoUrl.js';

// Same-origin everywhere. Locally the Vue dev server proxies /api to scripts/dev-api-server.mjs.
const baseURL = '/api';

const api = axios.create({
  baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Debug flag - set to true to enable verbose API logging
const DEBUG_API = false; // Set to true for debugging API calls

// Request interceptor with auth token
api.interceptors.request.use(
  async (config) => {
    if (DEBUG_API) {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    // Add auth token if available
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor with token refresh handling
api.interceptors.response.use(
  (response) => {
    if (DEBUG_API) {
      console.log(`API Response: ${response.status} ${response.config.url}`);
    }
    
    // Debug: Log search/taxonomies responses to check profile_pic_url
    return response;
  },
  async (error) => {
    // Also covers platform-generated 5xx where the API handler could not report.
    if ((!error.response || error.response.status >= 500) && error.code !== 'ERR_CANCELED') {
      captureError(error, { source: 'network', endpoint: error.config?.url, code: error.code,
        method: error.config?.method, statusCode: error.response?.status,
        requestId: error.response?.headers?.['x-vercel-id'] });
    }
    showLlmBudgetNotice(error.response?.data);
    console.error('API Response Error:', error.response?.data || error.message);
    
    // Handle 401 errors by attempting token refresh
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const fullUrl = error.config?.baseURL ? `${error.config.baseURL}${requestUrl}` : requestUrl;
      const urlToCheck = fullUrl || requestUrl;

      // Strip query string for reliable path matching
      let pathname;
      try { pathname = new URL(urlToCheck, window.location.origin).pathname; }
      catch { pathname = urlToCheck.split('?')[0]; }

      const isPublicEndpoint = /\/public(\/|$)/.test(pathname) ||
                               /\/shared(\/|$)/.test(pathname);

      const embedPaths = ['/cut30embed-compact', '/cut30-embed', '/cut30-chart-embed', '/cut30-chart-embed-2', '/embed-test', '/cut30-content-review', '/report/'];
      const isEmbedPage = embedPaths.some(p => window.location.pathname.startsWith(p));
      const isAuthPage = ['/signup', '/login', '/auth'].some(p => window.location.pathname.startsWith(p));

      if (isPublicEndpoint || isEmbedPage || isAuthPage) {
        return Promise.reject(error);
      }

      try {
        const { authManager } = await import('./auth.js');

        // Never redirect users who were never logged in
        if (!authManager.isAuthenticated) {
          return Promise.reject(error);
        }

        // Try to refresh the session
        await authManager.refreshSession();

        // Retry the original request with new token
        const originalRequest = error.config;
        const token = authManager.getToken();
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } else {
          window.location.href = '/login';
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

const normalizeResponseVideos = (response) => {
  if (!response || !response.data) {
    return response;
  }

  if (Array.isArray(response.data)) {
    response.data = normalizeVideoCollection(response.data);
    return response;
  }

  if (response.data && typeof response.data === 'object') {
    const nested = response.data.data;

    if (Array.isArray(nested)) {
      response.data = {
        ...response.data,
        data: normalizeVideoCollection(nested),
      };
      return response;
    }

    if (nested && typeof nested === 'object') {
      response.data = {
        ...response.data,
        data: normalizeVideoCollection(nested),
      };
      return response;
    }
  }

  return response;
};

const withVideoNormalization = (promise) =>
  promise.then((response) => normalizeResponseVideos(response));

export const videoApi = {
  // Generic HTTP methods for custom endpoints
  get(url, config = {}) {
    // Increase timeout for feed endpoint (complex queries can take longer)
    if (url.includes('/feed')) {
      return withVideoNormalization(api.get(url, { ...config, timeout: 60000 })); // 60 seconds
    }
    // Increase timeout for weekly hook analysis (AI generation takes time)
    if (url.includes('/updates/test-hooks-week')) {
      return withVideoNormalization(api.get(url, { ...config, timeout: 120000 })); // 120 seconds (2 minutes)
    }
    return withVideoNormalization(api.get(url, config));
  },

  post(url, data, config = {}) {
    return api.post(url, data, config);
  },

  put(url, data, config = {}) {
    return api.put(url, data, config);
  },

  delete(url, config = {}) {
    return api.delete(url, config);
  },

  // Get all videos with pagination and filters (increased timeout for complex queries)
  getVideos(params = {}) {
    return withVideoNormalization(api.get('/videos', { 
      params,
      timeout: 60000 // 60 seconds for complex queries
    }));
  },

  // Get specific video by ID
  getVideo(id) {
    return withVideoNormalization(api.get(`/videos/${id}`));
  },

  // Search videos
  searchVideos(query, params = {}) {
    return withVideoNormalization(api.get('/videos/search', { 
      params: { q: query, ...params } 
    }));
  },

  // Get analytics
  getAnalytics() {
    return api.get('/analytics');
  },

  // Get topics
  getTopics() {
    return api.get('/topics');
  },

  // Get concepts
  getConcepts() {
    return api.get('/concepts');
  },

  // Get elements
  getElements() {
    return api.get('/elements');
  },

  // Get formats
  getFormats() {
    return api.get('/formats');
  },

  // Get account types
  getAccountTypes() {
    return api.get('/account-types');
  },

  // Health check
  getHealth() {
    return api.get('/health');
  },

  // Public endpoints for VideoDB Beta (increased timeout for complex queries)
  getPublicVideos(params = {}) {
    return withVideoNormalization(api.get('/videos/public', { 
      params,
      timeout: 60000 // 60 seconds for complex queries
    }));
  },

  getPublicVideo(id) {
    return withVideoNormalization(api.get(`/videos/public/${id}`));
  },

  getPublicHookDominance(ids = []) {
    return api.get('/videos/public/hook-dominance', {
      params: { ids: ids.join(',') }
    });
  },

  getPublicRelatedVideos(id, params = {}) {
    return withVideoNormalization(api.get(`/videos/public/${id}/related`, { params }));
  },

  // Chat History API
  getChatHistory(conversationId = null) {
    const params = conversationId ? { conversation_id: conversationId } : {};
    return api.get('/chat-history', { params });
  },

  createChatConversation(title, messages = []) {
    return api.post('/chat-history', { title, messages });
  },

  updateChatConversation(conversationId, title = null, messages = null) {
    return api.put('/chat-history', { conversation_id: conversationId, title, messages });
  },

  deleteChatConversation(conversationId) {
    return api.delete('/chat-history', { params: { conversation_id: conversationId } });
  },

  getPublicTopics() {
    return api.get('/topics/public');
  },

  getTopicTopVideos(slug, params = {}) {
    return withVideoNormalization(api.get(`/topics/public/slug/${slug}/top`, { params }));
  },

  getPublicConcepts() {
    return api.get('/concepts/public');
  },

  getConceptTopVideos(slug, params = {}) {
    return withVideoNormalization(api.get(`/concepts/public/slug/${slug}/top`, { params }));
  },

  getPublicElements() {
    return api.get('/elements/public');
  },

  getElementTopVideos(slug, params = {}) {
    return withVideoNormalization(api.get(`/elements/public/slug/${slug}/top`, { params }));
  },

  getPublicFormats() {
    return api.get('/formats/public');
  },

  getFormatTopVideos(slug, params = {}) {
    return withVideoNormalization(api.get(`/formats/public/slug/${slug}/top`, { params }));
  },

  getPublicAccountTypes() {
    return api.get('/account-types/public');
  },

  // Admin competitor endpoints
  getCompetitorTargets(params = {}) {
    return api.get('/admin/competitors/targets', { params });
  },

  getCompetitorVideos(params = {}) {
    return api.get('/admin/competitors/videos', { params });
  },

  getCompetitorPosts(params = {}) {
    return api.get('/admin/competitors/posts', { params });
  },

  getAccountTypeTopVideos(slug, params = {}) {
    return withVideoNormalization(api.get(`/account-types/public/slug/${slug}/top`, { params }));
  },

  // Target API methods
  getTargetBySlug(slug) {
    return api.get(`/targets/public/${slug}`);
  },

  getTargetTopVideos(slug, params = {}) {
    return withVideoNormalization(api.get(`/targets/public/${slug}/top`, { params }));
  },

  getTargetVideos(slug, params = {}) {
    return withVideoNormalization(api.get(`/targets/public/${slug}/videos`, { params }));
  },

  getTargetPosts(slug, params = {}) {
    return api.get(`/targets/public/${slug}/posts`, { params });
  },

  getTargetTaxonomies(slug) {
    return api.get(`/targets/public/${slug}/taxonomies`);
  },

  getSimilarTargets(slug, params = {}) {
    return api.get(`/targets/public/${slug}/similar`, { params });
  },

  // Creator API methods
  getCreatorBySlug(slug) {
    return api.get(`/creators/public/${slug}`);
  },

  getCreatorStats(slug) {
    return api.get(`/creators/public/${slug}/stats`);
  },

  getCreatorVideos(slug, params = {}) {
    return withVideoNormalization(api.get(`/creators/public/${slug}/videos`, { params }));
  },

  getSimilarCreators(slug, params = {}) {
    return api.get(`/creators/public/${slug}/similar`, { params });
  },

  getCreatorTaxonomies(slug) {
    return api.get(`/creators/public/${slug}/taxonomies`);
  },

  getCreatorTopVideos(slug, params = {}) {
    return api.get(`/creators/public/${slug}/top-videos`, { params }).then(response => {
      const grouped = response.data?.data;
      if (grouped && typeof grouped === 'object' && !Array.isArray(grouped)) {
        const normalized = {};
        for (const [platform, videos] of Object.entries(grouped)) {
          normalized[platform] = Array.isArray(videos) ? normalizeVideoCollection(videos) : videos;
        }
        response.data = { ...response.data, data: normalized };
      }
      return response;
    });
  },

  getCreatorUsernameMap() {
    return api.get('/creators/username-map');
  },

  // Public creators list (paginated, no auth)
  getPublicCreators(params = {}) {
    return api.get('/creators/list', { params });
  },

  // Creator Admin API methods
  getCreators(params = {}) {
    return api.get('/creators', { params });
  },

  createCreator(data) {
    return api.post('/creators', data);
  },

  updateCreator(id, data) {
    return api.put(`/creators/${id}`, data);
  },

  uploadCreatorImage(payload) {
    return api.post('/admin/upload-creator-image', payload);
  },

  deleteCreator(id) {
    return api.delete(`/creators/${id}`);
  },

  runCreatorAutoMatch() {
    return api.post('/creators/auto-match', {}, { timeout: 90000 }); // 90s; bulk operation can be slow with many creators
  },

  getCreatorSuggestions() {
    return api.get('/creators/suggestions');
  },

  declineCreatorSuggestion(creatorAId, creatorBId) {
    return api.post('/creators/suggestions/decline', {
      creator_a_id: creatorAId,
      creator_b_id: creatorBId
    });
  },

  declineLinkSuggestion(creatorId, targetIds) {
    return api.post('/creators/suggestions/decline', {
      type: 'link',
      creator_id: creatorId,
      target_ids: targetIds
    });
  },

  approveLinkSuggestion(creatorId, targetIds) {
    return api.post('/creators/suggestions/approve-link', { creator_id: creatorId, target_ids: targetIds });
  },

  approveCreateSuggestion(suggestedSlug, targetIds) {
    return api.post('/creators/suggestions/approve-create', { suggested_slug: suggestedSlug, target_ids: targetIds });
  },

  searchTargetsForCreator(q) {
    return api.get('/admin/targets/search', { params: { q: q || '' } });
  },

  getAccountTypeSuggestions() {
    return api.get('/admin/account-type-suggestions');
  },

  getAdminNotifications() {
    return api.get('/admin/notifications');
  },

  confirmAccountTypeSuggestion(suggestionId, accountTypeId) {
    return api.post('/admin/account-type-suggestions/confirm', {
      suggestion_id: suggestionId,
      account_type_id: accountTypeId
    });
  },

  deleteTargetDescription(targetId) {
    return api.post('/admin/delete-target-description', { target_id: targetId });
  },

  getArchetypeReview() {
    return api.get('/admin/archetype-review');
  },

  confirmArchetypeReview(targetId, archetypeId) {
    return api.post('/admin/archetype-review/confirm', {
      target_id: targetId,
      archetype_id: archetypeId,
    });
  },

  classifyUnknownAccountTypes(limit = 20) {
    return api.post('/admin/classify-unknown-account-types', { limit });
  },

  backfillTargetDescriptions(limit = 30) {
    return api.post('/admin/backfill-target-descriptions', { limit });
  },

  mergeCreators(keepId, mergeId) {
    return api.post('/creators/merge', { keep_id: keepId, merge_id: mergeId });
  },

  getTaxonomyTargets(type, slug) {
    return api.get(`/taxonomies/${type}/${slug}/targets`);
  },

  getTaxonomyHooks(type, slug) {
    return api.get(`/taxonomies/${type}/${slug}/hooks`);
  },

  getTaxonomyRelatedTopics(type, slug) {
    return api.get(`/taxonomies/${type}/${slug}/related-topics`);
  },

  getTaxonomyRelatedConcepts(type, slug) {
    return api.get(`/taxonomies/${type}/${slug}/related-concepts`);
  },

  getTaxonomyRelatedFormats(type, slug) {
    return api.get(`/taxonomies/${type}/${slug}/related-formats`);
  },

  // Cut30 Students API
  getCut30Students(params = {}) {
    return api.get('/cut30/students', { params });
  },

  // Cut30 Creators API - embeddable creator showcase
  getCut30Creators(params = {}) {
    return api.get('/cut30/creators', { params });
  },

  // Cut30 Private Posts API (deprecated - use individual endpoints below)
  getCut30PrivatePosts(params = {}) {
    return api.get('/cut30/private-posts', { params });
  },

  // Cut30 Stats API - Lightweight stats for header
  getCut30Stats(params = {}) {
    return api.get('/cut30/stats', { params });
  },

  // Cut30 Posts API - Paginated posts for Latest tab
  getCut30Posts(params = {}) {
    return api.get('/cut30/posts', { params });
  },

  // Cut30 Popular API - Top posts for Popular tab
  getCut30Popular(params = {}) {
    return api.get('/cut30/popular', { params });
  },

  // Cut30 Top Posts API - Paginated top posts for expanded view
  getCut30TopPosts(params = {}) {
    return api.get('/cut30/top-posts', { params });
  },

  // Cut30 Accounts API - Account list for Accounts tab
  getCut30Accounts(params = {}) {
    return api.get('/cut30/accounts', { params });
  },

  // Cut30 Leaderboard API - Account leaderboard with stats
  getCut30Leaderboard(params = {}) {
    return api.get('/cut30/leaderboard', { params });
  },

  // Remove false positive from Cut30 tracking (admin only)
  removeCut30FalsePositive(targetId) {
    return api.post('/cut30/remove-false-positive', { targetId });
  },

  // Verify target as legitimate Cut30 student (admin only)
  verifyCut30Student(targetId) {
    return api.post('/cut30/verify-student', { targetId });
  },

  // Toggle public/private status for Cut30 student
  toggleCut30Public(targetId, isPublic) {
    return api.post('/cut30/toggle-public', { targetId, isPublic });
  },

  // Strategy Audit
  getStrategyAuditCreators() {
    return api.get('/insights/strategy-audit-creators');
  },

  getStrategyAudit(creator, params = {}) {
    return api.post('/insights/strategy-audit', { creator, ...params }, { timeout: 300000 });
  },

  getStrategyAuditStatus(reportId) {
    return api.get('/insights/strategy-audit-status', { params: { id: reportId } });
  },

  postStrategyAuditCommentary(data) {
    return api.post('/insights/strategy-audit-commentary', data, { timeout: 300000 });
  },

  getStrategyAuditQueue(params = {}) {
    return api.get('/insights/strategy-audit-queue', { params });
  },

  getStrategyAuditSessions(params = {}) {
    return api.get('/insights/strategy-audit-sessions', { params });
  },

  enqueueStrategyAuditQueue(items, options = {}) {
    return api.post('/insights/strategy-audit-queue/enqueue', { items, ...options });
  },

  retryStrategyAuditQueue(ids) {
    return api.post('/insights/strategy-audit-queue/retry', { ids });
  },

  cancelStrategyAuditQueue(ids) {
    return api.post('/insights/strategy-audit-queue/cancel', { ids });
  },

  sendStrategyAuditEmail(payload) {
    return api.post('/insights/strategy-audit-email/send', payload, { timeout: 300000 });
  },

  getStrategyAuditEmailQueue(params = {}) {
    return api.get('/insights/strategy-audit-email-queue', { params });
  },

  enqueueStrategyAuditEmailQueue(payload) {
    return api.post('/insights/strategy-audit-email-queue/enqueue', payload);
  },

  sendStrategyAuditEmailQueue(payload = {}) {
    return api.post('/insights/strategy-audit-email-queue/send', payload, { timeout: 300000 });
  },

  approveAndSendStrategyAuditEmail(payload = {}) {
    return api.post('/insights/strategy-audit-email-queue/approve-send', payload, { timeout: 300000 });
  },

  rejectStrategyAuditEmailApproval(payload = {}) {
    return api.post('/insights/strategy-audit-email-queue/reject', payload);
  },

  retryStrategyAuditEmailQueue(ids) {
    return api.post('/insights/strategy-audit-email-queue/retry', { ids });
  },

  cancelStrategyAuditEmailQueue(ids) {
    return api.post('/insights/strategy-audit-email-queue/cancel', { ids });
  },

  getPublicFilters() {
    return api.get('/filters/public');
  },

  getPublicFilterCounts() {
    return api.get('/filters/public/counts');
  },

  // Taxonomy search for universal search results
  searchTaxonomies(query) {
    return api.get('/search/taxonomies', { params: { q: query } });
  },

  // Swipefiles API
  getSwipefiles(userId) {
    return api.get(`/swipefiles?user_id=${userId}`);
  },

  getSwipefile(id) {
    return api.get(`/swipefiles?id=${id}`);
  },

  createSwipefile(swipefileData) {
    return api.post('/swipefiles', swipefileData);
  },

  updateSwipefile(id, updates) {
    return api.put(`/swipefiles?id=${id}`, updates);
  },

  deleteSwipefile(id) {
    return api.delete(`/swipefiles?id=${id}`);
  },

  // Saved Videos API
  getSavedVideos(userId) {
    return api.get(`/saved-videos?user_id=${userId}`);
  },

  getSavedVideosBySwipefile(swipefileId) {
    return api.get(`/saved-videos?swipefile_id=${swipefileId}`);
  },

  saveVideo(videoId, swipefileId, userId, { saveType, hookData } = {}) {
    const payload = {
      video_id: videoId,
      swipefile_id: swipefileId,
      user_id: userId
    };
    if (saveType) payload.save_type = saveType;
    if (hookData) payload.hook_data = hookData;
    return api.post('/saved-videos', payload);
  },

  unsaveVideo(videoId, swipefileId, userId) {
    return api.delete('/saved-videos', {
      data: {
        video_id: videoId,
        swipefile_id: swipefileId,
        user_id: userId
      }
    });
  },

  updateSavedVideoNotes(savedVideoId, notes) {
    return api.put(`/saved-videos/${savedVideoId}/notes`, { notes });
  },

  // Swipefile Sharing API
  getSwipefileCollaborators(swipefileId) {
    return api.get(`/swipefiles/${swipefileId}/collaborators`);
  },

  addSwipefileCollaborator(swipefileId, collaboratorData) {
    return api.post(`/swipefiles/${swipefileId}/collaborators`, collaboratorData);
  },

  inviteSwipefileCollaborator(swipefileId, collaboratorData) {
    return api.post(`/swipefiles/${swipefileId}/invite`, collaboratorData);
  },

  updateSwipefileCollaborator(swipefileId, collaboratorId, updates) {
    return api.put(`/swipefiles/${swipefileId}/collaborators/${collaboratorId}`, updates);
  },

  removeSwipefileCollaborator(swipefileId, collaboratorId) {
    return api.delete(`/swipefiles/${swipefileId}/collaborators/${collaboratorId}`);
  },

  enableSwipefileSharing(swipefileId, accessLevel) {
    return api.post(`/swipefiles/${swipefileId}/share`, { access_level: accessLevel });
  },

  // Shared Swipefile API
  getSharedSwipefile(token) {
    return api.get(`/shared/swipefiles/${token}`);
  },

  getPublicSwipefile(slug) {
    return api.get(`/public/swipefiles/${slug}`);
  },

  getSharedSwipefiles() {
    return api.get('/swipefiles/shared');
  },

  deleteSavedVideo(id) {
    return api.delete(`/saved-videos?id=${id}`);
  },

  // Video Save Status API
  getVideoSaveStatus(videoId, userId) {
    return api.get(`/video-save-status?video_id=${videoId}&user_id=${userId}`);
  },

  // Views API
  getViews() {
    return api.get('/views/public');
  },

  getView(slug) {
    return api.get(`/views/public?slug=${slug}`);
  },

  createView(viewData) {
    return api.post('/views/public', viewData);
  },

  updateView(id, updates) {
    return api.put(`/views/public?id=${id}`, updates);
  },

  deleteView(id) {
    return api.delete(`/views/public?id=${id}`);
  },

  // Admin: Update target page description
  updateTargetPageDescription(targetId, pageDescription) {
    return api.put(`/targets/${targetId}`, { page_description: pageDescription });
  },

  // Admin: Update taxonomy page description
  updateTaxonomyPageDescription(type, id, pageDescription) {
    return api.put(`/admin/taxonomies/${type}/${id}`, { page_description: pageDescription });
  },

  // Admin: Update taxonomy section intro text (preserves linked/mentions)
  updateTaxonomySectionIntro(type, id, sectionKey, text) {
    return api.put(`/admin/taxonomies/${type}/${id}`, {
      section_intros_update: { key: sectionKey, text }
    });
  },

  // Follows API
  getFollows() {
    return api.get('/follows');
  },

  unfollowEntity(entityType, entityId) {
    return api.delete('/follows', { params: { entityType, entityId } });
  },

  bulkFollow(follows) {
    return api.post('/follows/bulk', { follows });
  },

  // Topic taxonomies (popular concepts/formats for a topic)
  getTopicTaxonomies(slug) {
    return api.get(`/topics/public/slug/${slug}/taxonomies`);
  },

  getSimilarTopics(slug, limit = 2) {
    return api.get(`/topics/public/slug/${slug}/similar`, { params: { limit } });
  },

  getTopicCreators(slug, limit = 5) {
    return api.get(`/topics/public/slug/${slug}/creators`, { params: { limit } });
  },

  // Interest onboarding status
  getOnboardingStatus() {
    return api.get('/onboarding-status');
  },
  updateOnboardingStatus(action) {
    return api.post('/onboarding-status', { action });
  },
  parseInterests(text, accountTypeIds = []) {
    return api.post('/interests/parse', { text, accountTypeIds });
  },

  // Feed summary (per-account activity stats)
  getFeedSummary() {
    return api.get('/feed-summary');
  },

  // Feed insights (curated sections for Insights tab)
  getFeedInsights() {
    return api.get('/feed-insights');
  }
};

export default api;
